import { FC } from 'react';

import { ContentCopy } from '@mui/icons-material';
import {
  Typography,
  Grid,
  Box,
  Modal,
  Fade,
  Paper,
  IconButton,
  OutlinedInput,
  InputAdornment,
  outlinedInputClasses,
  InputLabel,
  FormControl,
  styled,
} from '@mui/material';
import {
  FacebookShareButton,
  RedditShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  FacebookIcon,
  RedditIcon,
  TelegramIcon,
  TwitterIcon,
  WhatsappIcon,
} from 'react-share';

import { useAnalytics } from '../../hooks/dataTracking/useAnalytics';
import { X as XIcon } from '../../icons/x';
import { IProposal } from '../../interfaces/proposal';
import { copyTextToClipboard } from '../../utils/copyToClipboard';

const OutlinedInputStyled = styled(OutlinedInput)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  [`& .${outlinedInputClasses.input}.${outlinedInputClasses.disabled}`]: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    textAlign: 'left',
    direction: 'rtl',
  },
}));

interface ShareModalProps {
  open: boolean;
  handleClose: () => void;
  proposal: IProposal;
}

export const ShareModal: FC<ShareModalProps> = ({ open, handleClose, proposal }) => {
  const { sendButtonClickTrackingEvent } = useAnalytics();
  const title = `Pegaxy Proposal: ${proposal.title}`;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropProps={{
        timeout: 100,
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: { xs: 'center', md: 'baseline' },
            justifyContent: 'center',
            padding: { xs: 0, md: 4 },
            marginTop: { xs: 0, md: 14 },
            m: 3,
          }}
        >
          <Paper
            elevation={12}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              margin: { xs: 0, md: 3 },
              minHeight: 300,
              maxHeight: '100%',
              outline: 'none',
              width: '100%',
              maxWidth: 440,
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                p: 3,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6">Share Proposal</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton onClick={handleClose}>
                <XIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                px: 3,
                py: 3,
                flexGrow: 1,
                overflow: 'auto',
              }}
            >
              <Grid container spacing={3} alignItems="center" justifyContent="center">
                <Grid item>
                  <FacebookShareButton
                    url={window.location.href}
                    quote={title}
                    onClick={() => sendButtonClickTrackingEvent('Share proposal to Facebook')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                      <FacebookIcon borderRadius={50} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Facebook
                      </Typography>
                    </Box>
                  </FacebookShareButton>
                </Grid>
                <Grid item>
                  <RedditShareButton
                    url={window.location.href}
                    title={title}
                    onClick={() => sendButtonClickTrackingEvent('Share proposal to Reddit')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                      <RedditIcon borderRadius={50} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Reddit
                      </Typography>
                    </Box>
                  </RedditShareButton>
                </Grid>
                <Grid item>
                  <TwitterShareButton
                    url={window.location.href}
                    onClick={() => sendButtonClickTrackingEvent('Share proposal to Twitter')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                      <TwitterIcon borderRadius={50} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Twitter
                      </Typography>
                    </Box>
                  </TwitterShareButton>
                </Grid>
                <Grid item>
                  <TelegramShareButton
                    url={window.location.href}
                    title={title}
                    onClick={() => sendButtonClickTrackingEvent('Share proposal to Telegram')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                      <TelegramIcon borderRadius={50} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Telegram
                      </Typography>
                    </Box>
                  </TelegramShareButton>
                </Grid>
                <Grid item>
                  <WhatsappShareButton
                    url={window.location.href}
                    title={title}
                    onClick={() => sendButtonClickTrackingEvent('Share proposal to Whatsapp')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                      <WhatsappIcon borderRadius={50} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Whatsapp
                      </Typography>
                    </Box>
                  </WhatsappShareButton>
                </Grid>
              </Grid>
              <FormControl sx={{ mt: 3, width: '100%' }}>
                <InputLabel>Copy link</InputLabel>
                <OutlinedInputStyled
                  fullWidth
                  label="Copy link"
                  value={window.location.href}
                  inputProps={{ color: 'red' }}
                  disabled
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          copyTextToClipboard(window.location.href);
                          sendButtonClickTrackingEvent('Share proposal via link')
                        }}
                        edge="end"
                      >
                        <ContentCopy />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Modal>
  );
};
